/**
 * The beatability validator for a dungeon plan's layout block — the contract
 * story S1 of `q-dungeon-layout-generator-build` (AD-404;
 * docs/design/dungeon-layout-generation.md §5). Every code is one invariant,
 * one check and one failing fixture; the layout stage (S2) is built against it.
 *
 * Pure and client-local (O-025): no wire, no combat maths, no reward values.
 */
import { DUNGEON_CHAMBER_SHAPES, DUNGEON_CORRIDOR_BANDS, DUNGEON_PACING_BEATS, DUNGEON_ROOM_ROLES, } from './types.js';
/**
 * Mirror of the player-body constants in `shared/src/sim/constants.ts`
 * (`PLAYER_COLLISION_RADIUS_M`, `DODGE_SPEED_MPS`, `DODGE_DURATION_MS`). The
 * package takes no workspace dependency on shared; `layout.test.ts` reads that
 * file and fails if a value drifts, so a movement change re-validates every
 * dungeon by construction (§5 "two rules about the numbers").
 */
export const DUNGEON_PLAYER_BODY = Object.freeze({
    collisionRadiusM: 0.4,
    dodgeSpeedMps: 12,
    dodgeDurationMs: 300,
});
/** Side margin a choke keeps beyond the player's diameter (`chokeClearWidth` floor). */
export const DUNGEON_CHOKE_MARGIN_M = 0.4;
/** `kFree.min` may never be authored below this (§5). */
export const DUNGEON_K_FREE_FLOOR = 1;
export function dodgeTravelM(player = DUNGEON_PLAYER_BODY) {
    return (player.dodgeSpeedMps * player.dodgeDurationMs) / 1000;
}
export function chokeClearWidthFloor(player = DUNGEON_PLAYER_BODY) {
    return 2 * player.collisionRadiusM + DUNGEON_CHOKE_MARGIN_M;
}
export const DUNGEON_LAYOUT_CODES = [
    'layout-overlap',
    'layout-connectivity',
    'critical-path-clearance',
    'choke-run',
    'dodge-clearance',
    'free-floor',
    'headroom',
    'secret-gap',
    'arena-entry',
    'directed',
    'compactness',
    'layout-determinism',
];
/** Layout coordinates are canonical at millimetre precision (`layout-determinism`). */
export const DUNGEON_LAYOUT_PRECISION = 1000;
const EPS = 1e-6;
const TOL = 1.5 / DUNGEON_LAYOUT_PRECISION;
// ---------------------------------------------------------------- geometry
export function polygonArea(points) {
    let twice = 0;
    for (let i = 0; i < points.length; i += 1) {
        const a = points[i];
        const b = points[(i + 1) % points.length];
        twice += a.x * b.z - b.x * a.z;
    }
    return twice / 2;
}
function pointSegmentDistance(p, a, b) {
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lengthSq = dx * dx + dz * dz;
    const t = lengthSq === 0 ? 0 : Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.z - a.z) * dz) / lengthSq));
    return Math.hypot(p.x - (a.x + t * dx), p.z - (a.z + t * dz));
}
function edges(points) {
    return points.map((point, index) => [point, points[(index + 1) % points.length]]);
}
function onBoundary(p, outline) {
    return edges(outline).some(([a, b]) => pointSegmentDistance(p, a, b) <= TOL);
}
function strictlyInside(p, outline) {
    if (onBoundary(p, outline))
        return false;
    let inside = false;
    for (const [a, b] of edges(outline)) {
        if (a.z > p.z !== b.z > p.z && p.x < ((b.x - a.x) * (p.z - a.z)) / (b.z - a.z) + a.x) {
            inside = !inside;
        }
    }
    return inside;
}
function insideOrOn(p, outline) {
    return onBoundary(p, outline) || strictlyInside(p, outline);
}
/** Distance from `p` to the nearest outline edge (the clear disc radius at `p`). */
function clearRadiusAt(p, outline) {
    return Math.min(...edges(outline).map(([a, b]) => pointSegmentDistance(p, a, b)));
}
function project(points, ax, az) {
    let min = Infinity;
    let max = -Infinity;
    for (const p of points) {
        const value = p.x * ax + p.z * az;
        if (value < min)
            min = value;
        if (value > max)
            max = value;
    }
    return [min, max];
}
/** Separating-axis test: true when two convex polygons share positive area (touching is not overlap). */
export function convexOverlap(a, b) {
    for (const polygon of [a, b]) {
        for (const [p, q] of edges(polygon)) {
            const length = Math.hypot(q.x - p.x, q.z - p.z);
            if (length === 0)
                continue;
            const ax = -(q.z - p.z) / length;
            const az = (q.x - p.x) / length;
            const [aMin, aMax] = project(a, ax, az);
            const [bMin, bMax] = project(b, ax, az);
            if (aMax <= bMin + TOL || bMax <= aMin + TOL)
                return false;
        }
    }
    return true;
}
function anyOverlap(a, b) {
    return a.some((pa) => b.some((pb) => convexOverlap(pa, pb)));
}
/** The walkable rectangle of one corridor segment. */
export function segmentFootprint(segment) {
    const { from, to, clearWidth } = segment;
    const length = Math.hypot(to.x - from.x, to.z - from.z);
    if (length === 0)
        return [from, from, from, from];
    const nx = (-(to.z - from.z) / length) * (clearWidth / 2);
    const nz = ((to.x - from.x) / length) * (clearWidth / 2);
    return [
        { x: from.x - nx, z: from.z - nz },
        { x: to.x - nx, z: to.z - nz },
        { x: to.x + nx, z: to.z + nz },
        { x: from.x + nx, z: from.z + nz },
    ];
}
function segmentLength(segment) {
    return Math.hypot(segment.to.x - segment.from.x, segment.to.z - segment.from.z);
}
function connectionEnds(context, connectionId) {
    const connection = context.plan.connections.find((entry) => entry.id === connectionId);
    return connection ? [connection.a, connection.b] : null;
}
/** The corridor segment that opens into `roomId` (that chamber's doorway) and the opening point. */
function doorway(context, corridor, roomId) {
    const ends = connectionEnds(context, corridor.connectionId);
    if (!ends || corridor.segments.length === 0)
        return undefined;
    if (ends[0] === roomId)
        return { segment: corridor.segments[0], opening: corridor.segments[0].from };
    if (ends[1] === roomId) {
        const last = corridor.segments[corridor.segments.length - 1];
        return { segment: last, opening: last.to };
    }
    return undefined;
}
function corridorsAt(context, roomId) {
    return context.layout.corridors.filter((corridor) => {
        const ends = connectionEnds(context, corridor.connectionId);
        return ends !== null && (ends[0] === roomId || ends[1] === roomId);
    });
}
function corridorBetween(context, a, b) {
    return context.layout.corridors.find((corridor) => {
        const ends = connectionEnds(context, corridor.connectionId);
        return ends !== null && ((ends[0] === a && ends[1] === b) || (ends[0] === b && ends[1] === a));
    });
}
/** Resolves a member's footprint: authored fields over the catalogue role default. */
export function resolveEnemyFootprint(enemyId, memberRoles, rules) {
    const enemy = rules.enemies.find((entry) => entry.id === enemyId);
    const roles = [...memberRoles, ...(enemy?.roles ?? [])];
    const base = roles.map((role) => rules.footprintDefaults[role]).find(Boolean);
    const authored = enemy?.footprint ?? {};
    const radius = authored.radius ?? base?.radius;
    const height = authored.height ?? base?.height;
    const telegraphReach = authored.telegraphReach ?? base?.telegraphReach;
    if (radius === undefined || height === undefined || telegraphReach === undefined)
        return undefined;
    return { radius, height, telegraphReach };
}
function encounterFootprints(context, roomId, report) {
    const encounter = context.roleById.get(roomId)?.encounter;
    if (!encounter)
        return null;
    const out = [];
    for (const member of encounter.members) {
        const footprint = resolveEnemyFootprint(member.enemyId, member.roles, context.rules);
        if (!footprint) {
            report(`rooms.${roomId}`, `enemy '${member.enemyId}' has no footprint and no role default`);
            continue;
        }
        out.push({ count: member.count, footprint });
    }
    return out;
}
// ---------------------------------------------------------------- the checks
const CHECKS = {
    'layout-overlap': (context, report) => {
        const { chambers, corridors, secret } = context.layout;
        for (let i = 0; i < chambers.length; i += 1) {
            for (let j = i + 1; j < chambers.length; j += 1) {
                if (anyOverlap(chambers[i].convex, chambers[j].convex)) {
                    report(`layout.chambers[${i}]`, `chamber '${chambers[i].roomId}' overlaps '${chambers[j].roomId}'`);
                }
            }
        }
        for (const [index, corridor] of corridors.entries()) {
            const ends = connectionEnds(context, corridor.connectionId) ?? ['', ''];
            const footprints = corridor.segments.map(segmentFootprint);
            for (const chamber of chambers) {
                if (ends.includes(chamber.roomId))
                    continue;
                if (anyOverlap(footprints, chamber.convex)) {
                    report(`layout.corridors[${index}]`, `corridor '${corridor.connectionId}' passes through chamber '${chamber.roomId}'`);
                }
            }
        }
        const alcove = [secret.alcove];
        for (const chamber of chambers) {
            if (anyOverlap(alcove, chamber.convex)) {
                report('layout.secret.alcove', `secret alcove overlaps chamber '${chamber.roomId}'`);
            }
        }
        for (const corridor of corridors) {
            if (anyOverlap(alcove, corridor.segments.map(segmentFootprint))) {
                report('layout.secret.alcove', `secret alcove overlaps corridor '${corridor.connectionId}'`);
            }
        }
    },
    'layout-connectivity': (context, report) => {
        const { plan, layout } = context;
        for (const room of plan.rooms) {
            if (!context.chamberById.has(room.id)) {
                report('layout.chambers', `room '${room.id}' has no chamber`);
            }
        }
        for (const chamber of layout.chambers) {
            if (!context.roleById.has(chamber.roomId)) {
                report('layout.chambers', `chamber '${chamber.roomId}' names no plan room`);
            }
        }
        for (const connection of plan.connections) {
            if (!context.corridorById.has(connection.id)) {
                report('layout.corridors', `connection '${connection.id}' has no corridor`);
            }
        }
        const adjacency = new Map();
        for (const [index, corridor] of layout.corridors.entries()) {
            const path = `layout.corridors[${index}]`;
            const ends = connectionEnds(context, corridor.connectionId);
            if (!ends) {
                report(path, `corridor names unknown connection '${corridor.connectionId}'`);
                continue;
            }
            if (corridor.segments.length === 0) {
                report(path, 'corridor has no segments');
                continue;
            }
            let joined = true;
            for (let s = 1; s < corridor.segments.length; s += 1) {
                const prev = corridor.segments[s - 1].to;
                const next = corridor.segments[s].from;
                if (Math.hypot(prev.x - next.x, prev.z - next.z) > TOL)
                    joined = false;
            }
            if (!joined)
                report(path, 'corridor segments are not contiguous');
            const start = corridor.segments[0].from;
            const end = corridor.segments[corridor.segments.length - 1].to;
            const a = context.chamberById.get(ends[0]);
            const b = context.chamberById.get(ends[1]);
            const opensA = a !== undefined && insideOrOn(start, a.outline);
            const opensB = b !== undefined && insideOrOn(end, b.outline);
            if (!opensA || !opensB) {
                report(path, `corridor does not open into '${ends[0]}' and '${ends[1]}'`);
                continue;
            }
            if (joined) {
                adjacency.set(ends[0], [...(adjacency.get(ends[0]) ?? []), ends[1]]);
                adjacency.set(ends[1], [...(adjacency.get(ends[1]) ?? []), ends[0]]);
            }
        }
        const seen = new Set([plan.entryRoomId]);
        const queue = [plan.entryRoomId];
        while (queue.length > 0) {
            for (const next of adjacency.get(queue.shift()) ?? []) {
                if (!seen.has(next)) {
                    seen.add(next);
                    queue.push(next);
                }
            }
        }
        for (const chamber of layout.chambers) {
            if (!seen.has(chamber.roomId)) {
                report('layout.chambers', `chamber '${chamber.roomId}' is not reachable from the entry`);
            }
        }
    },
    'critical-path-clearance': (context, report) => {
        const { clearance } = context.rules;
        const path = context.plan.criticalPathRoomIds;
        const critical = new Set();
        for (let i = 1; i < path.length; i += 1) {
            const corridor = corridorBetween(context, path[i - 1], path[i]);
            if (corridor)
                critical.add(corridor.connectionId);
        }
        for (const [index, corridor] of context.layout.corridors.entries()) {
            for (const [s, segment] of corridor.segments.entries()) {
                const where = `layout.corridors[${index}].segments[${s}]`;
                if (critical.has(corridor.connectionId) &&
                    segment.clearWidth < clearance.chokeClearWidth - EPS) {
                    report(where, `critical-path width ${segment.clearWidth} < chokeClearWidth ${clearance.chokeClearWidth}`);
                }
                if (segment.kind === 'normal' &&
                    segment.clearWidth < context.rules.doorwayClearWidth - EPS) {
                    report(where, `width ${segment.clearWidth} < doorwayClearWidth ${context.rules.doorwayClearWidth} and not a declared choke`);
                }
            }
        }
    },
    'choke-run': (context, report) => {
        const { clearance } = context.rules;
        for (const [index, corridor] of context.layout.corridors.entries()) {
            let run = 0;
            for (const segment of corridor.segments) {
                run = segment.kind === 'choke' ? run + segmentLength(segment) : 0;
                if (run > clearance.chokeMaxRunLength + EPS) {
                    report(`layout.corridors[${index}]`, `choke runs ${run.toFixed(3)} m > chokeMaxRunLength ${clearance.chokeMaxRunLength}`);
                    break;
                }
            }
            const ends = connectionEnds(context, corridor.connectionId);
            const hasChoke = corridor.segments.some((segment) => segment.kind === 'choke');
            if (ends && hasChoke) {
                for (const end of ends) {
                    const role = context.roleById.get(end)?.role;
                    if (role === 'boss' || role === 'miniboss') {
                        report(`layout.corridors[${index}]`, `a choke enters the ${role} chamber '${end}'`);
                    }
                }
            }
        }
        const path = context.plan.criticalPathRoomIds;
        let consecutive = 0;
        for (let i = 1; i < path.length; i += 1) {
            const corridor = corridorBetween(context, path[i - 1], path[i]);
            consecutive = corridor?.segments.some((segment) => segment.kind === 'choke')
                ? consecutive + 1
                : 0;
            if (consecutive > clearance.maxConsecutiveChokes) {
                report('layout.corridors', `${consecutive} choke corridors in a row on the critical path > ${clearance.maxConsecutiveChokes}`);
                break;
            }
        }
    },
    'dodge-clearance': (context, report) => {
        const player = context.player;
        for (const [index, chamber] of context.layout.chambers.entries()) {
            const path = `layout.chambers[${index}]`;
            if (!strictlyInside(chamber.centre, chamber.outline)) {
                report(path, `centre of '${chamber.roomId}' is not inside its outline`);
                continue;
            }
            const clear = clearRadiusAt(chamber.centre, chamber.outline);
            if (chamber.inscribedRadius > clear + TOL) {
                report(path, `declared inscribedRadius ${chamber.inscribedRadius} overstates the clear disc ${clear.toFixed(3)}`);
            }
            const members = encounterFootprints(context, chamber.roomId, report);
            if (members === null)
                continue;
            const reach = Math.max(0, ...members.map((member) => member.footprint.telegraphReach));
            const required = reach > 0
                ? reach + dodgeTravelM(player) + player.collisionRadiusM
                : context.rules.chamberMinInscribedRadius;
            if (chamber.inscribedRadius < required - EPS) {
                report(path, `'${chamber.roomId}' inscribedRadius ${chamber.inscribedRadius} < R_dodge ${required.toFixed(3)}`);
            }
        }
    },
    'free-floor': (context, report) => {
        const { kFree } = context.rules.clearance;
        const { standingAllowance } = context.rules;
        const r = context.player.collisionRadiusM;
        for (const [index, chamber] of context.layout.chambers.entries()) {
            const members = encounterFootprints(context, chamber.roomId, () => undefined);
            if (members === null)
                continue;
            const occupied = members.reduce((sum, member) => sum + member.count * Math.PI * (member.footprint.radius + standingAllowance.enemy) ** 2, 0) +
                context.plan.partySize * Math.PI * (r + standingAllowance.player) ** 2;
            const intensity = Math.max(0, Math.min(1, chamber.intensity));
            const k = Math.max(DUNGEON_K_FREE_FLOOR, kFree.max - intensity * (kFree.max - kFree.min));
            const area = Math.abs(polygonArea(chamber.outline));
            if (area < k * occupied - EPS) {
                report(`layout.chambers[${index}]`, `'${chamber.roomId}' floor ${area.toFixed(2)} m² < k_free ${k.toFixed(2)} × occupancy ${occupied.toFixed(2)} m²`);
            }
        }
    },
    headroom: (context, report) => {
        for (const [index, chamber] of context.layout.chambers.entries()) {
            const members = encounterFootprints(context, chamber.roomId, () => undefined);
            if (members === null || members.length === 0)
                continue;
            const tallest = Math.max(...members.map((member) => member.footprint.height));
            const required = tallest + context.rules.clearance.headroom;
            if (chamber.wallHeight < required - EPS) {
                report(`layout.chambers[${index}]`, `'${chamber.roomId}' wallHeight ${chamber.wallHeight} < ${required.toFixed(3)} (tallest member + headroom)`);
            }
        }
    },
    'secret-gap': (context, report) => {
        const { secret } = context.layout;
        const host = context.chamberById.get(secret.hostRoomId);
        const role = context.roleById.get(secret.hostRoomId)?.role;
        if (!host || role === undefined) {
            report('layout.secret.hostRoomId', `secret host '${secret.hostRoomId}' is not a chamber`);
            return;
        }
        if (role === 'boss')
            report('layout.secret.hostRoomId', 'the secret may not be hosted by the boss chamber');
        const expected = context.rules.secret.entranceClearWidth - context.rules.secret.panelWidth;
        const width = segmentLength(secret.gap);
        if (Math.abs(width - expected) > TOL) {
            report('layout.secret.gap', `gap ${width.toFixed(3)} m ≠ secretEntranceClearWidth − secretPanelWidth = ${expected.toFixed(3)} m`);
        }
        const wall = edges(host.outline).find(([a, b]) => pointSegmentDistance(secret.gap.from, a, b) <= TOL &&
            pointSegmentDistance(secret.gap.to, a, b) <= TOL);
        if (!wall) {
            report('layout.secret.gap', 'gap does not lie on one wall of the host chamber');
        }
        else {
            for (const corridor of corridorsAt(context, secret.hostRoomId)) {
                const door = doorway(context, corridor, secret.hostRoomId);
                if (door && pointSegmentDistance(door.opening, wall[0], wall[1]) <= TOL) {
                    report('layout.secret.gap', `the secret's host wall carries doorway '${corridor.connectionId}'`);
                }
            }
        }
        if (secret.alcove.length < 3 || Math.abs(polygonArea(secret.alcove)) <= EPS) {
            report('layout.secret.alcove', 'alcove must be a closed polygon with positive area');
        }
        else if (!onBoundary(secret.gap.from, secret.alcove) ||
            !onBoundary(secret.gap.to, secret.alcove)) {
            report('layout.secret.alcove', 'the gap does not open into the alcove');
        }
    },
    'arena-entry': (context, report) => {
        const { plan } = context;
        for (const room of plan.rooms) {
            if (room.role !== 'boss' && room.role !== 'miniboss')
                continue;
            const index = plan.criticalPathRoomIds.indexOf(room.id);
            const previous = index > 0 ? plan.criticalPathRoomIds[index - 1] : undefined;
            const corridor = previous ? corridorBetween(context, previous, room.id) : undefined;
            if (!corridor)
                continue;
            if (corridor.segments.some((segment) => segment.kind !== 'normal' || segment.clearWidth < context.rules.doorwayClearWidth - EPS)) {
                report(`layout.corridors`, `the corridor into ${room.role} '${room.id}' is not full doorway width`);
            }
        }
        for (const connection of plan.connections.filter((entry) => entry.kind === 'sealed-gate')) {
            const ends = new Set([connection.a, connection.b]);
            if (!ends.has(plan.finalBossRoomId) || !ends.has(plan.exitRoomId)) {
                report(`connections.${connection.id}`, 'the sealed gate is not on the boss→exit corridor');
            }
            else if (!context.corridorById.has(connection.id)) {
                report(`connections.${connection.id}`, 'the sealed gate has no corridor');
            }
        }
    },
    directed: (context, report) => {
        const { plan } = context;
        const path = plan.criticalPathRoomIds;
        for (let i = 0; i + 1 < path.length; i += 1) {
            const exit = corridorBetween(context, path[i], path[i + 1]);
            const exitDoor = exit ? doorway(context, exit, path[i])?.segment : undefined;
            if (!exit || !exitDoor)
                continue;
            for (const other of corridorsAt(context, path[i])) {
                if (other === exit)
                    continue;
                const door = doorway(context, other, path[i])?.segment;
                if (door && door.clearWidth > exitDoor.clearWidth + EPS) {
                    report(`layout.chambers.${path[i]}`, `doorway '${other.connectionId}' (${door.clearWidth} m) is wider than the way forward (${exitDoor.clearWidth} m)`);
                }
            }
        }
        const critical = new Set(path);
        const depth = new Map(path.map((id) => [id, 0]));
        const queue = [...path];
        while (queue.length > 0) {
            const current = queue.shift();
            for (const connection of plan.connections) {
                const next = connection.a === current ? connection.b : connection.b === current ? connection.a : null;
                if (next === null || critical.has(next) || depth.has(next))
                    continue;
                depth.set(next, depth.get(current) + 1);
                queue.push(next);
            }
        }
        for (const [roomId, value] of depth) {
            if (value > context.rules.clearance.maxBranchDepth) {
                report(`rooms.${roomId}`, `branch depth ${value} > maxBranchDepth ${context.rules.clearance.maxBranchDepth}`);
            }
        }
    },
    compactness: (context, report) => {
        const { layout } = context;
        const { bounds } = layout;
        const width = bounds.maxX - bounds.minX;
        const depth = bounds.maxZ - bounds.minZ;
        const points = [
            ...layout.chambers.flatMap((chamber) => chamber.outline),
            ...layout.secret.alcove,
            ...layout.corridors.flatMap((corridor) => corridor.segments.flatMap(segmentFootprint)),
        ];
        if (points.some((p) => p.x < bounds.minX - TOL ||
            p.x > bounds.maxX + TOL ||
            p.z < bounds.minZ - TOL ||
            p.z > bounds.maxZ + TOL)) {
            report('layout.bounds', 'bounds do not enclose the layout');
        }
        if (width <= 0 || depth <= 0) {
            report('layout.bounds', 'bounds have no area');
            return;
        }
        const area = layout.chambers.reduce((sum, chamber) => sum + Math.abs(polygonArea(chamber.outline)), 0);
        const ratio = area / (width * depth);
        if (ratio < context.rules.compactnessFloor - EPS) {
            report('layout.metrics.compactness', `compactness ${ratio.toFixed(3)} < compactnessFloor ${context.rules.compactnessFloor}`);
        }
        if (Math.abs(layout.metrics.compactness - ratio) > 1e-3) {
            report('layout.metrics.compactness', `recorded ${layout.metrics.compactness} ≠ measured ${ratio.toFixed(3)}`);
        }
    },
    'layout-determinism': (context, report) => {
        const { plan, layout } = context;
        const roomOrder = plan.rooms.map((room) => room.id).filter((id) => context.chamberById.has(id));
        if (layout.chambers.map((chamber) => chamber.roomId).join('|') !== roomOrder.join('|')) {
            report('layout.chambers', 'chambers are not in plan.rooms order');
        }
        const connectionOrder = plan.connections
            .map((entry) => entry.id)
            .filter((id) => context.corridorById.has(id));
        if (layout.corridors.map((corridor) => corridor.connectionId).join('|') !==
            connectionOrder.join('|')) {
            report('layout.corridors', 'corridors are not in plan.connections order');
        }
        for (const chamber of layout.chambers) {
            if (chamber.floorY !== 0)
                report(`layout.chambers.${chamber.roomId}`, 'floorY is reserved and must be 0');
        }
        const offenders = [];
        const walk = (value, path) => {
            if (typeof value === 'number') {
                const scaled = value * DUNGEON_LAYOUT_PRECISION;
                if (!Number.isFinite(value) || Math.abs(scaled - Math.round(scaled)) > 1e-6)
                    offenders.push(path);
            }
            else if (Array.isArray(value)) {
                value.forEach((entry, index) => walk(entry, `${path}[${index}]`));
            }
            else if (typeof value === 'object' && value !== null) {
                for (const [key, entry] of Object.entries(value))
                    walk(entry, `${path}.${key}`);
            }
        };
        walk(layout, 'layout');
        if (offenders.length > 0) {
            report(offenders[0], `${offenders.length} layout number(s) are not canonical (finite, millimetre-rounded)`);
        }
    },
};
/**
 * The AD-404 §5 beatability validator. Same problem shape as
 * `validateDungeonPlan`, so it feeds the same retry ladder. A v1 plan (no
 * `layout`) yields one `layout-missing` problem — callers gate on the schema version.
 */
export function validateDungeonLayout(plan, rules, options = {}) {
    const problems = [];
    const layout = plan.layout;
    if (!layout) {
        return [
            { code: 'layout-missing', path: 'layout', message: 'plan has no layout block (schema v1)' },
        ];
    }
    const context = {
        plan,
        layout,
        rules,
        player: rules.player ?? DUNGEON_PLAYER_BODY,
        chamberById: new Map(layout.chambers.map((chamber) => [chamber.roomId, chamber])),
        corridorById: new Map(layout.corridors.map((corridor) => [corridor.connectionId, corridor])),
        roleById: new Map(plan.rooms.map((room) => [room.id, room])),
    };
    for (const code of options.codes ?? DUNGEON_LAYOUT_CODES) {
        CHECKS[code](context, (path, message) => problems.push({ code, path, message }));
    }
    return problems;
}
// ---------------------------------------------------------------- catalogue floors
function isRecord(value) {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}
function finite(value) {
    return typeof value === 'number' && Number.isFinite(value);
}
function band(value, path, push, lowest = 0) {
    if (!isRecord(value) || !finite(value.min) || !finite(value.max)) {
        push(path, 'must be { min, max } of finite numbers');
    }
    else if (value.min < lowest || value.max < value.min) {
        push(path, `must satisfy ${lowest} <= min <= max`);
    }
}
/** Validates an optional `clearance` block against its §5 floors. */
export function validateClearanceProfile(clearance, path, player = DUNGEON_PLAYER_BODY) {
    const problems = [];
    const push = (where, message) => problems.push({ code: 'invalid-clearance', path: where, message });
    if (!isRecord(clearance)) {
        push(path, 'must be an object');
        return problems;
    }
    const floor = chokeClearWidthFloor(player);
    if (!finite(clearance.chokeClearWidth) || clearance.chokeClearWidth < floor - EPS) {
        problems.push({
            code: 'clearance-floor',
            path: `${path}.chokeClearWidth`,
            message: `must be >= 2·PLAYER_COLLISION_RADIUS_M + margin = ${floor}`,
        });
    }
    if (!finite(clearance.chokeMaxRunLength) || clearance.chokeMaxRunLength <= 0) {
        push(`${path}.chokeMaxRunLength`, 'must be > 0');
    }
    if (!Number.isInteger(clearance.maxConsecutiveChokes) ||
        clearance.maxConsecutiveChokes < 0) {
        push(`${path}.maxConsecutiveChokes`, 'must be an integer >= 0');
    }
    band(clearance.kFree, `${path}.kFree`, push);
    if (isRecord(clearance.kFree) &&
        finite(clearance.kFree.min) &&
        clearance.kFree.min < DUNGEON_K_FREE_FLOOR) {
        problems.push({
            code: 'clearance-floor',
            path: `${path}.kFree.min`,
            message: `must be >= ${DUNGEON_K_FREE_FLOOR} (a fight needs floor, not just standing room)`,
        });
    }
    if (!finite(clearance.headroom) || clearance.headroom <= 0)
        push(`${path}.headroom`, 'must be > 0');
    if (!Number.isInteger(clearance.maxBranchDepth) || clearance.maxBranchDepth < 1) {
        push(`${path}.maxBranchDepth`, 'must be an integer >= 1');
    }
    return problems;
}
export function validatePacingProfile(pacing, path) {
    if (!isRecord(pacing) ||
        !Array.isArray(pacing.beats) ||
        pacing.beats.length === 0 ||
        pacing.beats.some((beat) => !DUNGEON_PACING_BEATS.includes(beat))) {
        return [
            {
                code: 'invalid-pacing',
                path,
                message: `beats must be a non-empty list of ${DUNGEON_PACING_BEATS.join('|')}`,
            },
        ];
    }
    return [];
}
export function validateThemeLayout(layout, path) {
    const problems = [];
    const push = (where, message) => problems.push({ code: 'invalid-theme-layout', path: where, message });
    if (!isRecord(layout)) {
        push(path, 'must be an object');
        return problems;
    }
    band(layout.turnBudgetDeg, `${path}.turnBudgetDeg`, push, -180);
    if (isRecord(layout.turnBudgetDeg) &&
        finite(layout.turnBudgetDeg.max) &&
        layout.turnBudgetDeg.max > 180) {
        push(`${path}.turnBudgetDeg.max`, 'must be <= 180');
    }
    if (!isRecord(layout.corridorLength)) {
        push(`${path}.corridorLength`, `must key ${DUNGEON_CORRIDOR_BANDS.join('|')}`);
    }
    else {
        for (const key of DUNGEON_CORRIDOR_BANDS) {
            band(layout.corridorLength[key], `${path}.corridorLength.${key}`, push, EPS);
        }
    }
    if (!finite(layout.chokeChance) || layout.chokeChance < 0 || layout.chokeChance > 1) {
        push(`${path}.chokeChance`, 'must be in [0, 1]');
    }
    if (!finite(layout.loopClosureDistance) || layout.loopClosureDistance <= 0) {
        push(`${path}.loopClosureDistance`, 'must be > 0');
    }
    if (!finite(layout.compactnessFloor) ||
        layout.compactnessFloor <= 0 ||
        layout.compactnessFloor > 1) {
        push(`${path}.compactnessFloor`, 'must be in (0, 1]');
    }
    if (!isRecord(layout.roles)) {
        push(`${path}.roles`, 'must key every room role');
        return problems;
    }
    for (const role of DUNGEON_ROOM_ROLES) {
        const entry = layout.roles[role];
        const where = `${path}.roles.${role}`;
        if (!isRecord(entry)) {
            push(where, 'missing');
            continue;
        }
        if (!Array.isArray(entry.shapes) ||
            entry.shapes.length === 0 ||
            entry.shapes.some((shape) => !DUNGEON_CHAMBER_SHAPES.includes(shape))) {
            push(`${where}.shapes`, `must be a non-empty list of ${DUNGEON_CHAMBER_SHAPES.join('|')}`);
        }
        band(entry.aspect, `${where}.aspect`, push, 1);
        if (!finite(entry.volumeFactor) || entry.volumeFactor <= 0)
            push(`${where}.volumeFactor`, 'must be > 0');
    }
    return problems;
}
export function validateFootprint(footprint, path, partial) {
    const problems = [];
    if (!isRecord(footprint)) {
        return [{ code: 'invalid-footprint', path, message: 'must be an object' }];
    }
    for (const [key, positive] of [
        ['radius', true],
        ['height', true],
        ['telegraphReach', false],
    ]) {
        const value = footprint[key];
        if (value === undefined && partial)
            continue;
        if (!finite(value) || (positive ? value <= 0 : value < 0)) {
            problems.push({
                code: 'invalid-footprint',
                path: `${path}.${key}`,
                message: positive ? 'must be > 0' : 'must be >= 0',
            });
        }
    }
    return problems;
}
//# sourceMappingURL=layout.js.map